import React from "react";

export interface IcpTarget {
  industry: string;
  teamSize: string;
  currentTool: string;
  decisionMaker: string;
  painSignal: string;
  geography: string;
}

export const DEFAULT_ICP_TARGET: IcpTarget = {
  industry: "Healthcare, Events, Retail, Field Ops",
  teamSize: "8 – 200 people",
  currentTool: "Spreadsheets, paper, When I Work",
  decisionMaker: "Ops Manager, HR Lead, Owner",
  painSignal: "Compliance, multi-location, certifications",
  geography: "US, UK, EU, ANZ",
};

/** Summary stats for Actual Signup Profile (derived or mock). */
export interface SignupProfileSummary {
  topIndustries: string;
  avgTeamSize: string;
  icpMatchRate: string;
  topRoles: string;
  topGeos: string;
  icpActivation: string;
  aiInsight: string;
}

const DEFAULT_SIGNUP_PROFILE: SignupProfileSummary = {
  topIndustries: "Healthcare (29%), Events (21%), Childcare (14%)",
  avgTeamSize: "6.2 (below ICP target of 8+)",
  icpMatchRate: "50% (7/14 active signups)",
  topRoles: "Ops Manager (36%), Owner (29%), HR (14%)",
  topGeos: "US 36%, EU 29%, UK 14%, Asia 21%",
  icpActivation: "ICP users: 67% activate vs 12% non-ICP",
  aiInsight:
    "ICP users activate 5.6× better. Your non-ICP signups (solo testers, spam) waste funnel capacity. Consider: (1) add team-size qualifier to signup, (2) healthcare + events are strongest — double down on those channels, (3) avg team size 6.2 is below your ≥8 threshold — consider lowering to ≥5 or adding a \"growing team\" segment.",
};

export interface IcpAnalysisTileProps {
  targetIcp: IcpTarget;
  onEditIcp: () => void;
  signupProfile?: SignupProfileSummary | null;
  onOpenSignupProfile: () => void;
}

export function IcpAnalysisTile({
  targetIcp,
  onEditIcp,
  signupProfile = null,
  onOpenSignupProfile,
}: IcpAnalysisTileProps) {
  const profile = signupProfile ?? DEFAULT_SIGNUP_PROFILE;

  return (
    <div className="icp-section">
      <div className="sec-hdr">
        ICP Analysis <span className="sec-badge">✦ Firecrawl-enriched</span>
      </div>
      <div className="icp-wrap">
        <div
          className="icp-card"
          onClick={onEditIcp}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onEditIcp()}
          role="button"
          tabIndex={0}
        >
          <h3>
            🎯 Target ICP <span style={{ fontSize: 10, color: "var(--t4)", fontWeight: 400 }}>✎ Edit</span>
          </h3>
          <div className="icp-row">
            <span className="ir-l">Industry</span>
            <span className="ir-v editable">{targetIcp.industry}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Team Size</span>
            <span className="ir-v editable">{targetIcp.teamSize}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Current Tool</span>
            <span className="ir-v editable">{targetIcp.currentTool}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Decision Maker</span>
            <span className="ir-v editable">{targetIcp.decisionMaker}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Pain Signal</span>
            <span className="ir-v editable">{targetIcp.painSignal}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Geography</span>
            <span className="ir-v editable">{targetIcp.geography}</span>
          </div>
        </div>
        <div
          className="icp-card"
          onClick={onOpenSignupProfile}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpenSignupProfile()}
          role="button"
          tabIndex={0}
        >
          <h3>
            📊 Actual Signup Profile <span className="fc-badge">🔥 Firecrawl</span>
          </h3>
          <div className="icp-row">
            <span className="ir-l">Top Industries</span>
            <span className="ir-v">{profile.topIndustries}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Avg Team Size</span>
            <span className="ir-v">{profile.avgTeamSize}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">ICP Match Rate</span>
            <span className="ir-v" style={{ color: "var(--amber-t)" }}>
              {profile.icpMatchRate}
            </span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Top Roles</span>
            <span className="ir-v">{profile.topRoles}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">Top Geos</span>
            <span className="ir-v">{profile.topGeos}</span>
          </div>
          <div className="icp-row">
            <span className="ir-l">ICP Activation</span>
            <span className="ir-v" style={{ color: "var(--green-t)" }}>
              {profile.icpActivation}
            </span>
          </div>
          <div className="icp-insight">
            <div className="ii-hdr">✦ AI Insight</div>
            <p>{profile.aiInsight}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
